/**
 * Classi, regole di riconoscimento e catalogo prodotti, modificabili.
 *
 * ## Perché questa pagina esiste
 *
 * Le regole di classificazione del prodotto sbagliano e sbaglieranno sempre. Un
 * console server Lantronix dichiara `sysServices` 79 — il bit di commutazione
 * acceso, perché commuta davvero fra porte seriali — e si legge «Switch».
 * Finora l'unica risposta era «apri una segnalazione e aspetta una versione»:
 * chi sapeva la risposta non aveva modo di scriverla, se non un CI alla volta.
 *
 * ## La cosa che la rende usabile
 *
 * L'anteprima. Una regola agisce su **tutta** la CMDB e a ogni scansione:
 * scriverla alla cieca e scoprire il risultato l'indomani, a inventario già
 * riscritto, sarebbe una funzione che nessuno usa due volte. Qui si vede prima
 * quanti CI prenderebbe, quanti ne cambierebbe davvero, e i primi venti per nome.
 */
import { useCallback, useState } from 'react'

import { api } from '../api/client'
import type {
  CustomFieldDefinition,
  CustomFieldType,
  CustomFieldWrite,
  EditableTaxonomy,
  RecognitionRule,
  RuleCondition,
  RuleDraft,
  RulePreview,
  SKU,
  SKUDraft,
} from '../api/types'
import { Card, EmptyState, ErrorBanner, Spinner } from '../components/common'
import { DataTable, type Column } from '../components/DataTable'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'

const SEZIONI = [
  { key: 'rules', label: () => t('Regole di riconoscimento') },
  { key: 'classes', label: () => t('Classi e categorie') },
  { key: 'skus', label: () => t('Catalogo prodotti (SKU)') },
  { key: 'fields', label: () => t('Campi personalizzati') },
] as const

type Sezione = (typeof SEZIONI)[number]['key']

/** Gli attributi che si usano più spesso in una condizione, come suggerimento. */
const ATTRIBUTI_FREQUENTI = [
  'sys_object_id',
  'sys_descr',
  'sys_services',
  'vendor',
  'model',
  'mac_vendor',
  'resource_type',
  'platform',
  'os',
]

const REGOLA_VUOTA: RuleDraft = {
  name: '',
  description: '',
  priority: 100,
  enabled: true,
  conditions: [{ attribute: 'sys_object_id', operator: 'prefix', value: '' }],
  assign_class: null,
  assign_category: null,
  assign_sku_id: null,
}

export default function Taxonomy() {
  const [sezione, setSezione] = useState<Sezione>('rules')
  const taxonomy = useAsync(() => api.editableTaxonomy(), [])

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Riconoscimento')}</h1>
          <p className="page__subtitle">
            {t('Classificazione degli oggetti e associazione ai prodotti')}
          </p>
        </div>
      </header>

      <nav className="tabs" role="tablist">
        {SEZIONI.map((s) => (
          <button
            key={s.key}
            role="tab"
            aria-selected={sezione === s.key}
            className={`tabs__tab${sezione === s.key ? ' tabs__tab--active' : ''}`}
            onClick={() => setSezione(s.key)}
          >
            {s.label()}
          </button>
        ))}
      </nav>

      {taxonomy.error && <ErrorBanner error={taxonomy.error} />}
      {taxonomy.loading && <Spinner />}
      {taxonomy.data && (
        <>
          {sezione === 'rules' && <Regole taxonomy={taxonomy.data} />}
          {sezione === 'classes' && (
            <Classi taxonomy={taxonomy.data} onChange={taxonomy.reload} />
          )}
          {sezione === 'skus' && <Catalogo taxonomy={taxonomy.data} />}
          {sezione === 'fields' && <CampiPersonalizzati taxonomy={taxonomy.data} />}
        </>
      )}
    </>
  )
}

// ----------------------------------------------------------------- le regole

function Regole({ taxonomy }: { taxonomy: EditableTaxonomy }) {
  const rules = useAsync(() => api.rules(), [])
  const skus = useAsync(() => api.skus(), [])
  const baseline = useAsync(() => api.baselineState(), [])
  const [bozza, setBozza] = useState<RuleDraft | null>(null)
  const [modificando, setModificando] = useState<string | null>(null)
  const [errore, setErrore] = useState<string | null>(null)

  const classi = taxonomy.categories.flatMap((c) => c.classes)

  const salva = async () => {
    if (!bozza) return
    setErrore(null)
    try {
      if (modificando) await api.updateRule(modificando, bozza)
      else await api.createRule(bozza)
      setBozza(null)
      setModificando(null)
      rules.reload()
    } catch (e) {
      setErrore(e instanceof Error ? e.message : String(e))
    }
  }

  const skuDi = (r: RecognitionRule) => (skus.data ?? []).find((s) => s.id === r.assign_sku_id)
  const colonneRegole: Column<RecognitionRule>[] = [
    { key: 'priorita', header: t('Priorità'), type: 'number', value: (r) => r.priority },
    {
      key: 'nome',
      header: t('Nome'),
      value: (r) => [t(r.name), r.description ? t(r.description) : ''].join(' '),
      render: (r) => (
        <>
          <strong>{t(r.name)}</strong>
          {r.description && <div className="muted">{t(r.description)}</div>}
        </>
      ),
    },
    {
      key: 'condizioni',
      header: t('Condizioni'),
      value: (r) => r.conditions.map((c) => `${c.attribute} ${c.operator} ${c.value}`).join('; '),
      render: (r) =>
        r.conditions.map((c, i) => (
          <div key={i} className="muted">
            <code>{c.attribute}</code> {c.operator} <code>{c.value}</code>
          </div>
        )),
    },
    {
      key: 'assegna',
      header: t('Assegna'),
      value: (r) => [r.assign_class ?? '', skuDi(r) ? `SKU ${skuDi(r)!.code}` : ''].filter(Boolean).join(' · '),
      render: (r) => {
        const sku = skuDi(r)
        return (
          <>
            {r.assign_class && <div>{r.assign_class}</div>}
            {sku && <div className="muted">SKU {sku.code}</div>}
          </>
        )
      },
    },
    {
      key: 'stato',
      header: t('Stato'),
      value: (r) => (r.enabled ? t('Attiva') : t('Disattivata')),
      render: (r) => (
        <>
          {r.enabled ? t('Attiva') : t('Disattivata')}
          {r.origin === 'baseline' && (
            <div className="muted" title={t('Arrivata con il prodotto; modificabile come le altre')}>
              {t('dalla baseline')}
            </div>
          )}
        </>
      ),
    },
    {
      key: 'azioni',
      header: '',
      sort: false,
      searchable: false,
      render: (r) => (
        <div style={{ textAlign: 'right' }}>
          <button
            className="secondary"
            onClick={() => {
              setModificando(r.id)
              const { id: _id, ...resto } = r
              setBozza(resto)
            }}
          >
            {t('Modifica')}
          </button>{' '}
          <button
            className="secondary"
            onClick={async () => {
              if (!window.confirm(t('Eliminare questa regola di classificazione? Non si può annullare.'))) return
              await api.deleteRule(r.id)
              rules.reload()
            }}
          >
            {t('Elimina')}
          </button>
        </div>
      ),
    },
  ]

  return (
    <>
      <Card
        title={t('Regole di riconoscimento')}
        actions={
          !bozza && (
            <button
              onClick={() => {
                setModificando(null)
                setBozza({ ...REGOLA_VUOTA, conditions: [...REGOLA_VUOTA.conditions] })
              }}
            >
              {t('Nuova regola')}
            </button>
          )
        }
      >
        <p className="muted" style={{ marginTop: 0 }}>
          {t('Hanno precedenza sulle regole della piattaforma e vengono applicate in ordine di priorità. La prima regola corrispondente viene utilizzata; le classificazioni manuali non vengono sovrascritte.')}
        </p>

        {errore && <ErrorBanner error={errore} />}

        {/* Le voci nuove della baseline. Senza questo avviso arriverebbero solo
            ai tenant creati dopo, e i clienti storici — quelli con più apparati —
            sarebbero gli ultimi ad averle. */}
        {(baseline.data?.pending_count ?? 0) > 0 && (
          <div className="banner">
            {t('Il prodotto ha {n} voci di riconoscimento che questo tenant non ha ancora. Applicarle non modifica né cancella niente di ciò che hai già.', { n: baseline.data?.pending_count })}{' '}
            <button
              onClick={async () => {
                await api.applyBaseline()
                baseline.reload()
                rules.reload()
              }}
            >
              {t('Applica')}
            </button>
          </div>
        )}

        {bozza && (
          <ModuloRegola
            bozza={bozza}
            setBozza={setBozza}
            taxonomy={taxonomy}
            classi={classi}
            skus={skus.data ?? []}
            onSalva={salva}
            onAnnulla={() => {
              setBozza(null)
              setModificando(null)
              setErrore(null)
            }}
          />
        )}

        {rules.loading && <Spinner />}
        {rules.data?.length === 0 && !bozza && (
          <EmptyState
            title={t('Nessuna regola')}
            hint={t("Le regole servono quando la piattaforma classifica male un apparato che tu riconosci a colpo d'occhio — per esempio dal sysObjectID del costruttore.")}
          />
        )}
        {rules.data && rules.data.length > 0 && (
          <DataTable
            rows={rules.data}
            columns={colonneRegole}
            rowKey={(r) => r.id}
            initialSort={{ key: 'priorita', dir: 'asc' }}
            conteggio={{ nome: t('regole') }}
          />
        )}
      </Card>
    </>
  )
}

function ModuloRegola({
  bozza,
  setBozza,
  taxonomy,
  classi,
  skus,
  onSalva,
  onAnnulla,
}: {
  bozza: RuleDraft
  setBozza: (r: RuleDraft) => void
  taxonomy: EditableTaxonomy
  classi: { key: string; label: string }[]
  skus: SKU[]
  onSalva: () => void
  onAnnulla: () => void
}) {
  const [anteprima, setAnteprima] = useState<RulePreview | null>(null)
  const [erroreAnteprima, setErroreAnteprima] = useState<string | null>(null)
  const [caricando, setCaricando] = useState(false)

  const aggiorna = (patch: Partial<RuleDraft>) => {
    setBozza({ ...bozza, ...patch })
    // L'anteprima è di **quella** regola: appena la regola cambia non descrive
    // più niente, e lasciarla sarebbe un numero rassicurante e falso.
    setAnteprima(null)
  }

  const condizione = (i: number, patch: Partial<RuleCondition>) => {
    const conditions = bozza.conditions.map((c, j) => (i === j ? { ...c, ...patch } : c))
    aggiorna({ conditions })
  }

  const prova = useCallback(async () => {
    setCaricando(true)
    setErroreAnteprima(null)
    try {
      setAnteprima(await api.previewRule(bozza))
    } catch (e) {
      setErroreAnteprima(e instanceof Error ? e.message : String(e))
    } finally {
      setCaricando(false)
    }
  }, [bozza])

  return (
    <div className="panel">
      <div className="form-grid">
        <label className="field">
          <span className="field__label">{t('Nome')}</span>
          <input
            className="input"
            value={bozza.name}
            onChange={(e) => aggiorna({ name: e.target.value })}
            placeholder={t('Console server Lantronix')}
          />
        </label>
        <label className="field">
          <span className="field__label">{t('Priorità')}</span>
          <input
            className="input"
            type="number"
            value={bozza.priority}
            onChange={(e) => aggiorna({ priority: Number(e.target.value) })}
          />
        </label>
      </div>

      <h4>{t('Condizioni')}</h4>
      <p className="muted" style={{ marginTop: 0 }}>
        {t('Devono essere vere tutte. Il confronto non distingue maiuscole e minuscole.')}
      </p>
      {bozza.conditions.map((c, i) => (
        <div key={i} className="form-row">
          <input
            className="input"
            list="attributi-frequenti"
            value={c.attribute}
            onChange={(e) => condizione(i, { attribute: e.target.value })}
            placeholder={t('attributo')}
          />
          <select
            className="input"
            value={c.operator}
            onChange={(e) => condizione(i, { operator: e.target.value })}
          >
            {taxonomy.operators.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </select>
          <input
            className="input"
            value={c.value}
            onChange={(e) => condizione(i, { value: e.target.value })}
            placeholder={t('valore')}
            disabled={c.operator === 'exists' || c.operator === 'missing'}
          />
          <button
            className="secondary"
            onClick={() =>
              aggiorna({ conditions: bozza.conditions.filter((_, j) => j !== i) })
            }
            aria-label={t('Togli la condizione')}
          >
            ×
          </button>
        </div>
      ))}
      <datalist id="attributi-frequenti">
        {[...ATTRIBUTI_FREQUENTI, ...taxonomy.ci_fields].map((a) => (
          <option key={a} value={a} />
        ))}
      </datalist>
      <button
        className="secondary"
        onClick={() =>
          aggiorna({
            conditions: [...bozza.conditions, { attribute: '', operator: 'equals', value: '' }],
          })
        }
      >
        {t('Aggiungi condizione')}
      </button>

      <h4>{t('Assegna')}</h4>
      <div className="form-grid">
        <label className="field">
          <span className="field__label">{t('Classe')}</span>
          <select
            className="input"
            value={bozza.assign_class ?? ''}
            onChange={(e) => aggiorna({ assign_class: e.target.value || null })}
          >
            <option value="">{t('— nessuna —')}</option>
            {classi.map((c) => (
              <option key={c.key} value={c.key}>
                {t(c.label)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field__label">{t('Prodotto a catalogo')}</span>
          <select
            className="input"
            value={bozza.assign_sku_id ?? ''}
            onChange={(e) => aggiorna({ assign_sku_id: e.target.value || null })}
          >
            <option value="">{t('— nessuno —')}</option>
            {skus.map((s) => (
              <option key={s.id} value={s.id}>
                {s.code} — {s.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {erroreAnteprima && <ErrorBanner error={erroreAnteprima} />}
      {anteprima && (
        <div className="panel">
          <p style={{ marginTop: 0 }}>
            {anteprima.protected_by_manual > 0
              ? t('Corrisponde a {n} CI, di cui {cambi} cambierebbero classe — {manuali} hanno la classe scelta a mano e non verrebbero toccati.', {
                  n: anteprima.matched,
                  cambi: anteprima.would_change_class,
                  manuali: anteprima.protected_by_manual,
                })
              : t('Corrisponde a {n} CI, di cui {cambi} cambierebbero classe.', {
                  n: anteprima.matched,
                  cambi: anteprima.would_change_class,
                })}
          </p>
          {anteprima.matched === 0 && (
            <p className="muted">
              {t("Nessuna corrispondenza: controlla il nome dell'attributo. Lo trovi in fondo alla scheda di un CI, sotto «Altri attributi».")}
            </p>
          )}
          <ul className="list">
            {anteprima.examples.map((e) => (
              <li key={e.id}>
                {e.name} <span className="muted">({e.ci_class ?? t('non classificato')})</span>
                {e.class_manual && <span className="muted"> · {t('scelta a mano')}</span>}
              </li>
            ))}
          </ul>
          {anteprima.truncated && <p className="muted">{t('…e altri.')}</p>}
        </div>
      )}

      <div className="form-actions">
        <button className="button" onClick={prova} disabled={caricando}>
          {caricando ? t('Provo…') : t('Prova senza salvare')}
        </button>{' '}
        <button onClick={onSalva}>
          {t('Salva')}
        </button>{' '}
        <button className="secondary" onClick={onAnnulla}>
          {t('Annulla')}
        </button>
      </div>
    </div>
  )
}

// ------------------------------------------------------ classi e categorie

function Classi({
  taxonomy,
  onChange,
}: {
  taxonomy: EditableTaxonomy
  onChange: () => void
}) {
  const [nuovaClasse, setNuovaClasse] = useState({ key: '', label: '', category: '' })
  const [nuovaCategoria, setNuovaCategoria] = useState({ key: '', label: '' })
  const [errore, setErrore] = useState<string | null>(null)

  const azione = async (fn: () => Promise<unknown>) => {
    setErrore(null)
    try {
      await fn()
      onChange()
    } catch (e) {
      setErrore(e instanceof Error ? e.message : String(e))
    }
  }

  type Classe = EditableTaxonomy['categories'][number]['classes'][number]
  const colonneClassi: Column<Classe>[] = [
    {
      key: 'classe',
      header: t('Classe'),
      value: (c) => `${t(c.label)} ${c.key}`,
      render: (c) => (
        <>
          <strong>{t(c.label)}</strong> <code className="muted">{c.key}</code>
        </>
      ),
    },
    {
      key: 'descrizione',
      header: t('Descrizione'),
      value: (c) => c.description,
      render: (c) => <span className="muted">{c.description}</span>,
    },
    {
      key: 'origine',
      header: t('Origine'),
      value: (c) => (c.custom ? t('personalizzata') : t('della piattaforma')),
    },
  ]

  return (
    <>
      {errore && <ErrorBanner error={errore} />}

      <Card title={t('Nuova classe')}>
        <p className="muted" style={{ marginTop: 0 }}>
          {t('Le classi predefinite della piattaforma non possono essere ridefinite. Classi con la stessa chiave e definizioni diverse comprometterebbero la coerenza di classificazione e filtraggio.')}
        </p>
        <div className="form-grid">
          <label className="field">
            <span className="field__label">{t('Chiave')}</span>
            <input
              className="input"
              value={nuovaClasse.key}
              onChange={(e) => setNuovaClasse({ ...nuovaClasse, key: e.target.value })}
              placeholder={t('console_server')}
            />
          </label>
          <label className="field">
            <span className="field__label">{t('Etichetta')}</span>
            <input
              className="input"
              value={nuovaClasse.label}
              onChange={(e) => setNuovaClasse({ ...nuovaClasse, label: e.target.value })}
              placeholder={t('Console server')}
            />
          </label>
          <label className="field">
            <span className="field__label">{t('Categoria')}</span>
            <select
              className="input"
              value={nuovaClasse.category}
              onChange={(e) => setNuovaClasse({ ...nuovaClasse, category: e.target.value })}
            >
              <option value="">{t('— scegli —')}</option>
              {taxonomy.categories.map((c) => (
                <option key={c.key} value={c.key}>
                  {t(c.label)}
                </option>
              ))}
            </select>
          </label>
        </div>
        <button
          
          disabled={!nuovaClasse.key || !nuovaClasse.label || !nuovaClasse.category}
          onClick={() =>
            azione(async () => {
              await api.createClass(nuovaClasse)
              setNuovaClasse({ key: '', label: '', category: '' })
            })
          }
        >
          {t('Aggiungi classe')}
        </button>
      </Card>

      <Card title={t('Nuova categoria')}>
        <div className="form-grid">
          <label className="field">
            <span className="field__label">{t('Chiave')}</span>
            <input
              className="input"
              value={nuovaCategoria.key}
              onChange={(e) => setNuovaCategoria({ ...nuovaCategoria, key: e.target.value })}
              placeholder={t('oob')}
            />
          </label>
          <label className="field">
            <span className="field__label">{t('Etichetta')}</span>
            <input
              className="input"
              value={nuovaCategoria.label}
              onChange={(e) =>
                setNuovaCategoria({ ...nuovaCategoria, label: e.target.value })
              }
              placeholder={t('Fuori banda')}
            />
          </label>
        </div>
        <button
          
          disabled={!nuovaCategoria.key || !nuovaCategoria.label}
          onClick={() =>
            azione(async () => {
              await api.createCategory(nuovaCategoria)
              setNuovaCategoria({ key: '', label: '' })
            })
          }
        >
          {t('Aggiungi categoria')}
        </button>
      </Card>

      {taxonomy.categories.map((categoria) => (
        <Card
          key={categoria.key}
          title={categoria.custom ? t('{categoria} · personalizzata', { categoria: categoria.label }) : t(categoria.label)}
        >
          <DataTable
            rows={categoria.classes}
            columns={colonneClassi}
            rowKey={(c) => c.key}
            conteggio={{ nome: t('classi') }}
            emptyText={t('Nessuna classe in questa categoria')}
            strumenti={false}
          />
        </Card>
      ))}
    </>
  )
}

// ------------------------------------------------------------ il catalogo

const SKU_VUOTO: SKUDraft = {
  code: '',
  label: '',
  vendor: null,
  model: null,
  description: '',
  ci_class: null,
  list_price: null,
  currency: 'EUR',
  attributes: {},
}

function Catalogo({ taxonomy }: { taxonomy: EditableTaxonomy }) {
  const skus = useAsync(() => api.skus(), [])
  const [bozza, setBozza] = useState<SKUDraft | null>(null)
  const [modificando, setModificando] = useState<string | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const classi = taxonomy.categories.flatMap((c) => c.classes)

  const salva = async () => {
    if (!bozza) return
    setErrore(null)
    try {
      if (modificando) await api.updateSku(modificando, bozza)
      else await api.createSku(bozza)
      setBozza(null)
      setModificando(null)
      skus.reload()
    } catch (e) {
      setErrore(e instanceof Error ? e.message : String(e))
    }
  }

  const colonneSku: Column<SKU>[] = [
    { key: 'code', header: t('Codice'), render: (s) => <code>{s.code}</code> },
    { key: 'label', header: t('Descrizione') },
    { key: 'vendor', header: t('Produttore') },
    { key: 'ci_class', header: t('Classe') },
    {
      key: 'list_price',
      header: t('Listino'),
      type: 'number',
      value: (s) => s.list_price,
      render: (s) =>
        s.list_price != null ? `${s.list_price.toLocaleString(localeCorrente())} ${s.currency ?? ''}` : '—',
    },
    {
      key: 'ci_count',
      header: t('CI agganciati'),
      type: 'number',
      value: (s) => s.ci_count,
      render: (s) => (
        <>
          {s.ci_count}
          {/* Zero quasi sempre significa che la regola che doveva
              riconoscerlo non corrisponde — e senza questa riga
              sembrerebbe semplicemente un prodotto non ancora in rete. */}
          {s.ci_count === 0 && <span className="muted"> · {t('nessuna regola lo aggancia?')}</span>}
        </>
      ),
    },
    {
      key: 'azioni',
      header: '',
      sort: false,
      searchable: false,
      render: (s) => (
        <div style={{ textAlign: 'right' }}>
          <button
            className="secondary"
            onClick={() => {
              const { id: _id, ci_count: _n, ...resto } = s
              setModificando(s.id)
              setBozza(resto)
            }}
          >
            {t('Modifica')}
          </button>{' '}
          <button
            className="secondary"
            onClick={async () => {
              if (!window.confirm(t('Eliminare questo SKU? Non si può annullare.'))) return
              await api.deleteSku(s.id)
              skus.reload()
            }}
          >
            {t('Elimina')}
          </button>
        </div>
      ),
    },
  ]

  return (
    <Card
      title={t('Catalogo prodotti')}
      actions={
        !bozza && (
          <button onClick={() => setBozza({ ...SKU_VUOTO })}>
            {t('Nuovo prodotto')}
          </button>
        )
      }
    >
      <p className="muted" style={{ marginTop: 0 }}>
        {t('Lo SKU identifica il prodotto associato a un oggetto. Viene assegnato ai CI tramite regole di riconoscimento e ne accompagna la trasformazione in asset.')}
      </p>

      {errore && <ErrorBanner error={errore} />}

      {bozza && (
        <div className="panel">
          <div className="form-grid">
            <label className="field">
              <span className="field__label">{t('Codice')}</span>
              <input
                className="input"
                value={bozza.code}
                onChange={(e) => setBozza({ ...bozza, code: e.target.value })}
                placeholder={t('SLC8016')}
              />
            </label>
            <label className="field">
              <span className="field__label">{t('Descrizione breve')}</span>
              <input
                className="input"
                value={bozza.label}
                onChange={(e) => setBozza({ ...bozza, label: e.target.value })}
                placeholder={t('Lantronix SLC 8000, 16 porte')}
              />
            </label>
            <label className="field">
              <span className="field__label">{t('Produttore')}</span>
              <input
                className="input"
                value={bozza.vendor ?? ''}
                onChange={(e) => setBozza({ ...bozza, vendor: e.target.value || null })}
              />
            </label>
            <label className="field">
              <span className="field__label">{t('Modello')}</span>
              <input
                className="input"
                value={bozza.model ?? ''}
                onChange={(e) => setBozza({ ...bozza, model: e.target.value || null })}
              />
            </label>
            <label className="field">
              <span className="field__label">{t('Classe')}</span>
              <select
                className="input"
                value={bozza.ci_class ?? ''}
                onChange={(e) => setBozza({ ...bozza, ci_class: e.target.value || null })}
              >
                <option value="">{t('— nessuna —')}</option>
                {classi.map((c) => (
                  <option key={c.key} value={c.key}>
                    {t(c.label)}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="field__label">{t('Listino')}</span>
              <input
                className="input"
                type="number"
                step="0.01"
                value={bozza.list_price ?? ''}
                onChange={(e) =>
                  setBozza({
                    ...bozza,
                    list_price: e.target.value === '' ? null : Number(e.target.value),
                  })
                }
              />
            </label>
          </div>
          <div className="form-actions">
            <button onClick={salva} disabled={!bozza.code}>
              {t('Salva')}
            </button>{' '}
            <button
              className="secondary"
              onClick={() => {
                setBozza(null)
                setModificando(null)
              }}
            >
              {t('Annulla')}
            </button>
          </div>
        </div>
      )}

      {skus.loading && <Spinner />}
      {skus.data?.length === 0 && !bozza && (
        <EmptyState
          title={t('Catalogo vuoto')}
          hint={t('Cataloga i modelli utilizzati nel parco asset. Le informazioni associate, come codice di ordinazione e costo, vengono applicate automaticamente quando un CI diventa un asset.')}
        />
      )}
      {skus.data && skus.data.length > 0 && (
        <DataTable
          rows={skus.data}
          columns={colonneSku}
          rowKey={(s) => s.id}
          conteggio={{ nome: t('prodotti') }}
        />
      )}
    </Card>
  )
}

// ------------------------------------------------- campi personalizzati (CI/Asset)

const TIPI_CAMPO: { key: CustomFieldType; label: () => string }[] = [
  { key: 'text', label: () => t('Testo') },
  { key: 'number', label: () => t('Numero') },
  { key: 'date', label: () => t('Data') },
  { key: 'boolean', label: () => t('Sì/No') },
  { key: 'select', label: () => t('Elenco a scelta') },
]

function bozzaVuota(entity: 'ci' | 'asset'): CustomFieldWrite & { id?: string } {
  return {
    entity,
    label: '',
    field_type: 'text',
    scope: null,
    required: false,
    options: [],
    help_text: '',
    position: 0,
  }
}

function CampiPersonalizzati({ taxonomy }: { taxonomy: EditableTaxonomy }) {
  const [entity, setEntity] = useState<'ci' | 'asset'>('ci')
  const defs = useAsync(() => api.customFields(), [])
  const assetCatalog = useAsync(() => api.assetCatalog(), [])
  const [bozza, setBozza] = useState<CustomFieldWrite & { id?: string }>(bozzaVuota('ci'))
  const [opzioniTesto, setOpzioniTesto] = useState('')
  const [busy, setBusy] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)

  // Gli ambiti selezionabili: le classi dei CI, o le categorie degli Asset.
  const classiCI = taxonomy.categories
    .flatMap((c) => c.classes)
    .map((k) => ({ key: k.key, label: k.label }))
  const categorieAsset = (assetCatalog.data?.categories ?? []).map((c) => ({
    key: c.value,
    label: c.label,
  }))
  const ambiti = entity === 'ci' ? classiCI : categorieAsset

  const dellEntita = (defs.data ?? []).filter((d) => d.entity === entity)

  function nuovo(perEntity: 'ci' | 'asset') {
    setEntity(perEntity)
    setBozza(bozzaVuota(perEntity))
    setOpzioniTesto('')
    setErrore(null)
  }

  function modifica(d: CustomFieldDefinition) {
    setEntity(d.entity)
    setBozza({
      id: d.id,
      entity: d.entity,
      label: d.label,
      field_type: d.field_type,
      scope: d.scope,
      required: d.required,
      options: d.options,
      help_text: d.help_text,
      position: d.position,
    })
    setOpzioniTesto(d.options.join('\n'))
    setErrore(null)
  }

  async function salva() {
    setBusy(true)
    setErrore(null)
    const payload: CustomFieldWrite = {
      entity: bozza.entity,
      label: bozza.label,
      field_type: bozza.field_type,
      scope: bozza.scope || null,
      required: bozza.required,
      options:
        bozza.field_type === 'select'
          ? opzioniTesto.split('\n').map((s) => s.trim()).filter(Boolean)
          : [],
      help_text: bozza.help_text,
    }
    try {
      if (bozza.id) await api.updateCustomField(bozza.id, payload)
      else await api.createCustomField(payload)
      nuovo(entity)
      defs.reload()
    } catch (err) {
      setErrore(err)
    } finally {
      setBusy(false)
    }
  }

  async function elimina(d: CustomFieldDefinition) {
    if (!window.confirm(t('Eliminare il campo «{label}»? I valori già scritti restano nei dati.', { label: d.label })))
      return
    await api.deleteCustomField(d.id)
    if (bozza.id === d.id) nuovo(entity)
    defs.reload()
  }

  const tipoDi = (d: CustomFieldDefinition) => TIPI_CAMPO.find((tp) => tp.key === d.field_type)?.label() ?? d.field_type
  const ambitoDi = (d: CustomFieldDefinition) => (d.scope ? ambiti.find((a) => a.key === d.scope)?.label ?? d.scope : '')
  const colonneCampi: Column<CustomFieldDefinition>[] = [
    {
      key: 'label',
      header: t('Etichetta'),
      render: (d) => (
        <>
          {d.label}
          {d.required && <span className="campocustom__obbligo" title={t('Obbligatorio')}> *</span>}
        </>
      ),
    },
    { key: 'tipo', header: t('Tipo'), value: tipoDi },
    {
      key: 'ambito',
      header: t('Ambito'),
      value: (d) => ambitoDi(d) || t('Tutte'),
      render: (d) => (d.scope ? ambitoDi(d) : <span className="muted">{t('Tutte')}</span>),
    },
    {
      key: 'azioni',
      header: '',
      sort: false,
      searchable: false,
      render: (d) => (
        <div className="rowactions">
          <button className="secondary" onClick={() => modifica(d)}>{t('Modifica')}</button>
          <button className="danger" onClick={() => elimina(d)}>{t('Elimina')}</button>
        </div>
      ),
    },
  ]

  return (
    <>
      <p className="page__subtitle">
        {t('Definisce campi personalizzati per l’inventario, oltre a quelli raccolti dalla discovery. Possono essere associati a una specifica classe di CI, a una categoria di asset oppure resi disponibili in tutte le schede.')}
      </p>

      <div className="tabs" role="tablist" style={{ marginBottom: 12 }}>
        <button
          role="tab"
          aria-selected={entity === 'ci'}
          className={`tabs__tab${entity === 'ci' ? ' tabs__tab--active' : ''}`}
          onClick={() => nuovo('ci')}
        >
          {t('Configuration Item')}
        </button>
        <button
          role="tab"
          aria-selected={entity === 'asset'}
          className={`tabs__tab${entity === 'asset' ? ' tabs__tab--active' : ''}`}
          onClick={() => nuovo('asset')}
        >
          {t('Asset')}
        </button>
      </div>

      <ErrorBanner error={defs.error ?? errore} />

      <div className="tax-griglia">
        <Card title={bozza.id ? t('Modifica campo') : t('Nuovo campo')}>
          <div className="field">
            <label htmlFor="cf-label">{t('Etichetta')}</label>
            <input
              id="cf-label"
              type="text"
              value={bozza.label}
              onChange={(e) => setBozza({ ...bozza, label: e.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor="cf-tipo">{t('Tipo')}</label>
            <select
              id="cf-tipo"
              value={bozza.field_type}
              onChange={(e) => setBozza({ ...bozza, field_type: e.target.value as CustomFieldType })}
            >
              {TIPI_CAMPO.map((tp) => (
                <option key={tp.key} value={tp.key}>{tp.label()}</option>
              ))}
            </select>
          </div>
          {bozza.field_type === 'select' && (
            <div className="field">
              <label htmlFor="cf-opzioni">{t('Valori ammessi (uno per riga)')}</label>
              <textarea
                id="cf-opzioni"
                rows={4}
                value={opzioniTesto}
                onChange={(e) => setOpzioniTesto(e.target.value)}
              />
            </div>
          )}
          <div className="field">
            <label htmlFor="cf-ambito">{t('Ambito')}</label>
            <select
              id="cf-ambito"
              value={bozza.scope ?? ''}
              onChange={(e) => setBozza({ ...bozza, scope: e.target.value || null })}
            >
              <option value="">{entity === 'ci' ? t('Tutte le classi') : t('Tutte le categorie')}</option>
              {ambiti.map((a) => (
                <option key={a.key} value={a.key}>{a.label}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label className="campocustom__switch">
              <input
                type="checkbox"
                checked={bozza.required}
                onChange={(e) => setBozza({ ...bozza, required: e.target.checked })}
              />
              <span>{t('Obbligatorio')}</span>
            </label>
          </div>
          <div className="field">
            <label htmlFor="cf-aiuto">{t('Aiuto (facoltativo)')}</label>
            <input
              id="cf-aiuto"
              type="text"
              value={bozza.help_text}
              onChange={(e) => setBozza({ ...bozza, help_text: e.target.value })}
            />
          </div>
          <div className="rowactions">
            <button onClick={salva} disabled={busy || !bozza.label.trim()}>
              {busy ? t('Salvo…') : bozza.id ? t('Salva modifiche') : t('Crea campo')}
            </button>
            {bozza.id && (
              <button className="secondary" onClick={() => nuovo(entity)}>
                {t('Annulla')}
              </button>
            )}
          </div>
        </Card>

        <Card title={t('Campi definiti')}>
          {defs.loading && !defs.data ? (
            <Spinner />
          ) : dellEntita.length === 0 ? (
            <EmptyState
              title={t('Nessun campo personalizzato')}
              hint={t('Crea un nuovo campo dal pannello di configurazione: sarà immediatamente disponibile nelle schede a cui è associato.')}
            />
          ) : (
            <DataTable
              rows={dellEntita}
              columns={colonneCampi}
              rowKey={(d) => d.id}
              conteggio={{ nome: t('campi') }}
            />
          )}
        </Card>
      </div>
    </>
  )
}
